import { mapGetters, mapActions } from 'vuex'

import parseData from '@/utils/parseData'
import KPI from '@/components/KPI/KPI.vue'

const TABLE_SAMPLE_LIMIT = 3

export const buildCompetitorTableRows = (rows = [], eligibleKeywordCount = 0) => {
  return rows.map((row, index) => ({
    ...row,
    position: index + 1,
    eligibleKeywordCount,
    keywordSampleText: Array.isArray(row.keywordSamples) ? row.keywordSamples.slice(0, TABLE_SAMPLE_LIMIT).join(', ') : '',
    urlSampleText: Array.isArray(row.urlSamples) ? row.urlSamples.slice(0, TABLE_SAMPLE_LIMIT).join(', ') : ''
  }))
}

export const summarizeCompetitors = (rows = [], meta = {}) => {
  const trackedRow = rows.find(row => row.isClientDomain) || null
  const trackedDomain = trackedRow?.domain || meta.clientDomain || 'Your domain'

  return {
    trackedDomain,
    trackedScore: trackedRow?.visibilityScore || 0,
    trackedKeywordCount: trackedRow?.keywordCount || 0,
    trackedAvgRank: trackedRow?.weightedAvgRank || 0,
    eligibleKeywordCount: meta.eligibleKeywordCount || 0,
    totalDomains: meta.totalDomains || rows.length
  }
}

export default {
  name: 'Competitors',
  components: { KPI },
  data () {
    return {
      isLoading: {
        report: false
      },
      report: {
        items: [],
        meta: {}
      },
      intent: 'transactional',
      intentOptions: [
        { text: 'Transactional', value: 'transactional' },
        { text: 'Informational', value: 'informational' },
        { text: 'Navigational', value: 'navigational' },
        { text: 'All intents', value: 'all' }
      ],
      tableHeaders: [
        { text: '#', value: 'position', width: 64 },
        { text: 'Domain', value: 'domain' },
        { text: 'Score', value: 'visibilityScore' },
        { text: 'Keywords', value: 'keywordCount' },
        { text: 'Avg rank', value: 'weightedAvgRank' },
        { text: 'URLs', value: 'urlCount' },
        { text: 'Volume', value: 'totalSearchVolume' },
        { text: 'Best rank', value: 'bestRank' },
        { text: 'Keyword samples', value: 'keywordSampleText', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan?.name || ''
    },
    rows () {
      return buildCompetitorTableRows(this.report.items || [], this.report.meta?.eligibleKeywordCount || 0)
    },
    summary () {
      return summarizeCompetitors(this.report.items || [], this.report.meta || {})
    },
    trackedScoreName () {
      return `${this.summary.trackedDomain} Score`
    },
    trackedKeywordName () {
      return `${this.summary.trackedDomain} Keywords`
    },
    trackedAvgRankName () {
      return `${this.summary.trackedDomain} Avg rank`
    },
    trackedKeywordSuffix () {
      return ` / ${parseData(this.summary.eligibleKeywordCount)}`
    }
  },
  methods: {
    parseData,
    ...mapActions([
      'contentplanGetOne',
      'competitorGet',
      'setSnackbar'
    ]),
    async loadData () {
      document.title = 'Competitors | ContentGecko'
      this.isLoading.report = true

      try {
        await this.contentplanGetOne({ contentplanId: this.contentplanId })
        await this.refreshCompetitors()
      } catch (error) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(error, 'Could not load competitors'), type: 'error' })
      } finally {
        this.isLoading.report = false
      }
    },
    async refreshCompetitors () {
      this.isLoading.report = true

      try {
        const response = await this.competitorGet({
          contentplanId: this.contentplanId,
          intent: this.intent
        })

        this.report = {
          items: Array.isArray(response?.items) ? response.items : [],
          meta: response?.meta || {}
        }
      } catch (error) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(error, 'Could not load competitor visibility'), type: 'error' })
      } finally {
        this.isLoading.report = false
      }
    },
    formatKeywordCoverage (item) {
      return `${parseData(item.keywordCount)} / ${parseData(item.eligibleKeywordCount)}`
    },
    formatRank (value) {
      if (!value) return '-'
      return parseData(value, 1)
    },
    getErrorMessage (error, fallback) {
      return error?.response?.data?.message || error?.message || fallback
    }
  },
  async mounted () {
    await this.loadData()
  }
}
